import { Local145FormOfPayment } from "app/calcs/customers/local145/interfaces"
import {
    AllSimplePensionFormsOfPayment,
    SimplePensionFormOfPayment,
} from "app/calcs/pensions/interfaces"
import { Profile } from "app/redux/Profile/model"
import * as utils from "app/utils"
import { ColumnNames, loadProfilePresets } from "config/145/ETL"
import {
    IBrand,
    IBrandHelpers,
    ICustomerMarkup,
    IPipelineConfig,
    IRegistrationInput,
    IWebConfig,
    IWebServicesConfig,
} from "config/interfaces"
import * as _ from "lodash"
import * as React from "react"

const Pipeline: IPipelineConfig = {
    Pension: {
        name: "Pension",
        path: "/pension/",
        icon: "fa fa-fw fa-calculator",
        thumbnailUrl: "/assets/img/pension.jpg",
        sections: [
            {
                name: "Pension Intro",
                icon: "",
                hidden: true,
                subsection: {
                    default: true,
                    hidePrevious: true,
                    hideNext: true,
                    hideNavRoutes: true,
                    questionsGroupName: "PensionIntro",
                },
            },
            {
                name: "Local 145 Pension",
                icon: "fa fa-fw fa-usd",
                subsection: {
                    questionsGroupName: "Local145PensionQuestionsGroup",
                },
            },
            {
                name: "NEBF Pension",
                icon: "fa fa-fw fa-usd",
                subsection: { questionsGroupName: "NEBFPensionQuestionsGroup" },
            },
            {
                name: "IO Pension",
                icon: "fa fa-fw fa-usd",
                subsection: { questionsGroupName: "IOPensionQuestionsGroup" },
            },
            {
                name: "Prior Pension",
                icon: "fa fa-fw fa-usd",
                subsection: {
                    questionsGroupName: "PriorPensionQuestionsGroup",
                },
            },
            {
                name: "Spouse Pension",
                icon: "fa fa-fw fa-usd",
                subsection: {
                    questionsGroupName: "SpousePensionQuestionsGroup",
                },
                needsSpouse: true,
            },
            {
                name: "Summary",
                icon: "fa fa-fw fa-balance-scale",
                subsection: {
                    hidePrevious: true,
                    hideNext: true,
                    questionsGroupName: "PensionSummary",
                },
            },
        ],
    },
    SocialSecurity: {
        name: "Social Security",
        path: "/socialsecurity/",
        icon: "fa fa-fw fa-calculator",
        thumbnailUrl: "/assets/img/socialsecurity.jpg",
        sections: [
            {
                name: "SSA Intro",
                icon: "",
                hidden: true,
                subsection: {
                    default: true,
                    hidePrevious: true,
                    hideNext: true,
                    hideNavRoutes: true,
                    questionsGroupName: "SSAIntro",
                },
            },
            {
                name: "SSA Benefits",
                icon: "fa fa-fw fa-calculator",
                subsection: {
                    hidePrevious: true,
                    hideNext: true,
                    questionsGroupName: "SSABenefits",
                },
            },
            {
                name: "Optimize",
                icon: "fa fa-fw fa-calculator",
                subsection: {
                    hidePrevious: true,
                    nextSectionRoutePath: "/retirement/0",
                    questionsGroupName: "SSAOptimizeQuestionsGroup",
                },
            },
        ],
    },
    Retirement: {
        name: "Savings",
        path: "/savings/",
        icon: "fa fa-fw fa-calculator",
        thumbnailUrl: "/assets/img/retirement.jpg",
        sections: [
            {
                name: "Retirement Intro",
                icon: "",
                hidden: true,
                subsection: {
                    default: true,
                    hidePrevious: true,
                    hideNext: true,
                    hideNavRoutes: true,
                    questionsGroupName: "RetirementIntro",
                },
            },
            {
                name: "Investment Return",
                icon: "fa fa-fw fa-calculator",
                subsection: {
                    questionsGroupName: "RetirementInvestmentReturns",
                },
            },
            {
                name: "Member",
                icon: "fa fa-fw fa-calculator",
                subsections: [
                    {
                        questionsGroupName:
                            "RetirementMemberContributionsQuestionsGroup",
                    },
                    {
                        questionsGroupName:
                            "RetirementMemberWithdrawalQuestionsGroup",
                    },
                ],
            },
            {
                name: "Spouse",
                icon: "fa fa-fw fa-calculator",
                subsections: [
                    {
                        questionsGroupName:
                            "RetirementSpouseContributionsQuestionsGroup",
                    },
                    {
                        questionsGroupName:
                            "RetirementSpouseWithdrawalQuestionsGroup",
                    },
                ],
                needsSpouse: true,
            },
            {
                name: "Summary",
                icon: "fa fa-fw fa-balance-scale",
                subsection: {
                    hidePrevious: true,
                    hideNext: true,
                    questionsGroupName: "RetirementSummary",
                },
            },
        ],
    },
    Expenses: {
        name: "Expenses",
        path: "/expenses/",
        icon: "fa fa-fw fa-calculator",
        thumbnailUrl: "/assets/img/expenses.jpg",
        sections: [
            {
                name: "Expenses Intro",
                icon: "",
                hidden: true,
                subsection: {
                    default: true,
                    hidePrevious: true,
                    hideNext: true,
                    hideNavRoutes: true,
                    questionsGroupName: "ExpensesIntro",
                },
            },
            {
                name: "Home Expenses",
                icon: "fa fa-fw fa-home",
                subsections: [
                    { questionsGroupName: "MortgageQuestionsGroup" },
                    { questionsGroupName: "PropertyTaxesQuestionsGroup" },
                    { questionsGroupName: "HomeInsuranceQuestionsGroup" },
                    { questionsGroupName: "HomeMaintanenceQuestionsGroup" },
                    { questionsGroupName: "UtilitiesQuestionsGroup" },
                ],
            },
            {
                name: "Auto",
                icon: "fa fa-fw fa-car",
                subsection: { questionsGroupName: "AutoQuestionsGroup" },
            },
            {
                name: "Food",
                icon: "fa fa-fw fa-cutlery",
                subsections: [
                    { questionsGroupName: "GroceriesQuestionsGroup" },
                    { questionsGroupName: "DiningOutQuestionsGroup" },
                ],
            },
            {
                name: "Clothing & Personal",
                icon: "fa fa-fw fa-bath",
                subsections: [
                    { questionsGroupName: "ClothingQuestionsGroup" },
                    { questionsGroupName: "WardrobeQuestionsGroup" },
                ],
            },
            {
                name: "Entertainment",
                icon: "fa fa-fw fa-ship",
                subsections: [
                    { questionsGroupName: "TravelQuestionsGroup" },
                    { questionsGroupName: "OtherEntertainmentQuestionsGroup" },
                ],
            },
            {
                name: "Other",
                icon: "fa fa-fw fa-briefcase",
                subsections: [
                    { questionsGroupName: "MedicalQuestionsGroup" },
                    { questionsGroupName: "CharityQuestionsGroup" },
                    { questionsGroupName: "TaxesQuestionsGroup" },
                    { questionsGroupName: "SavingsDebtQuestionsGroup" },
                ],
            },
            {
                name: "Summary",
                icon: "fa fa-fw fa-balance-scale",
                subsection: {
                    hidePrevious: true,
                    hideNext: true,
                    questionsGroupName: "ExpensesSummary",
                },
            },
        ],
    },
    Results: {
        name: "Retirement Summary",
        path: "/Results/",
        icon: "fa fa-fw fa-heartbeat",
        hiddenInDashboard: true,
        sections: [
            {
                name: "Retirement Summary",
                icon: "",
                hidden: true,
                subsection: {
                    default: true,
                    hidePrevious: true,
                    hideNext: true,
                    hideNavRoutes: true,
                    questionsGroupName: "Results",
                },
            },
        ],
    },
}

const productionServer = window.location.origin + "/"
const productionSSAServer = "https://ssa2.localpension.com/"
const WebServices: IWebServicesConfig = {
    accountsEndpoint: productionServer,
    familyInfoEndpoint: productionServer,
    ssaWorkbookEndpoint: productionSSAServer,
    ncpersWorkbookEndpoint: productionServer,
    testWorkbookEndpoint: productionServer,
    miscEndpoint: productionServer,
    workbookEndpoint: productionServer,
}

// if we're not been run in production, choose our web servers
if (!utils.isProductionEnvironment()) {
    const endpointMap = {
        themajordomo: "http://local145.themajordomo.com/",
        localpension: "https://local145.localpension.com/",
        dev: "http://local145.tjdoldserver.com:8001/",
    }
    const devServer = utils.selectWebservicesEndpoint(endpointMap)
    const devSSAServer = "http://ssa.tjdoldserver.com:8001/"
    _.merge(WebServices, {
        accountsEndpoint: devServer,
        familyInfoEndpoint: devServer,
        ssaWorkbookEndpoint: productionSSAServer,
        //ssaWorkbookEndpoint: devSSAServer,
        ncpersWorkbookEndpoint: devServer,
        testWorkbookEndpoint: devServer,
        miscEndpoint: devServer,
        workbookEndpoint: devServer,
    })
}

const RegistrationInputs: IRegistrationInput[] = [
    {
        name: "firstname",
        placeholder: "First Name",
        type: "text",
    },
    {
        name: "person.lastName",
        placeholder: "Last Name",
        type: "text",
    },
    {
        name: "person.uniqueID.numeric",
        placeholder: "Last 4 digits of SSN",
        type: "text",
    },
    {
        name: "username",
        placeholder: "Username",
        type: "text",
    },
    {
        name: "password",
        placeholder: "Create Password",
        type: "password",
    },
]

const underTableCss: React.CSSProperties = {
    color: "green",
    fontWeight: "bold",
}

const markup: ICustomerMarkup = {
    introduction: {
        intro1: (
            <div>
                <p className="lead">
                    A retirement planning calculator is a tool that can help you
                    plan your retirement. This calculator is provided to you as
                    an informational tool only. To get a reasonable picture of
                    what your retirement could look like, you will need to enter
                    information for the various sections including…
                </p>
                <ol className="lead">
                    <li>Pension benefits (including your Local 145 pension)</li>
                    <li>Social Security benefits</li>
                    <li>
                        Savings benefits (including your Local 145 Annuity and
                        Profit Sharing account)
                    </li>
                    <li>Personal expenses</li>
                </ol>
            </div>
        ),
        intro2: (
            <div>
                <br />
                <br />
                <p className="lead">
                    Keep in mind that these are reasonable estimates of your
                    benefit picture. All benefits shown are only estimates, and
                    should not be considered a promise to pay. The plan document
                    requirements for each individual benefit must be met in all
                    aspects in order to receive any benefit under any benefit
                    plan shown on this retirement calculator. To get actual
                    benefit amounts, you will need to talk to the administrators
                    of each of the benefits involved.
                </p>
            </div>
        ),
    },
    pension: {
        introduction: {
            intro1: (
                <div>
                    <h5 className="lead">
                        In this section, you’ll work through the pension
                        benefits that you have earned during your career,
                        including your Local 145 Pension. Each benefit is shown
                        as an annuity under the various optional forms of
                        payment provided from the plan. You will need to select
                        your point of retirement and form of payment for each
                        plan shown.
                    </h5>
                    <div className="lead">
                        Note the following:
                        <ul>
                            <li>
                                The benefits shown for each plan are preliminary
                                and should be considered as estimates until your
                                final calculations are prepared.
                            </li>
                            <li>
                                Your actual benefit will be reduced should you
                                have a Qualified Domestic Relations Order (QDRO)
                                attached to your benefit. Such reductions are
                                outside the scope of this retirement planning
                                calculator.
                            </li>
                        </ul>
                    </div>
                </div>
            ),
        },
        summary: {
            underTable: (
                <div className="lead" style={underTableCss}>
                    <br />
                    <p>
                        Note that your actual benefit will be reduced should you
                        have a Qualified Domestic Relations Order (QDRO)
                        attached to your benefit. Such reductions are outside
                        the scope of this retirement planning calculator.
                    </p>
                </div>
            ),
        },
    },
    retirement: {
        introduction: {
            intro1: (
                <div className="lead">
                    In this section, you’ll work through the various sources of
                    savings that you and your spouse have accumulated, including
                    your <strong>Local 145 Annuity and Profit Sharing</strong>{" "}
                    account. You will be able to incorporate estimated future
                    contributions, investment returns, and withdrawal amounts to
                    project how long your savings could last during retirement.
                    Keep in mind that this is a simple projection that models
                    future scenarios based on the assumptions you select. Actual
                    results will vary based on actual experience. For a more
                    sophisticated look at your future savings balances, you may
                    want to contact an investment professional.
                    <br />
                    <br />
                    Note that your actual benefit will be reduced should you
                    have a Qualified Domestic Relations Order (QDRO) attached to
                    your benefit. Such reductions are outside the scope of this
                    retirement planning calculator.
                </div>
            ),
        },
        summary: {
            underTable: (
                <div className="lead" style={underTableCss}>
                    <br />
                    <p>
                        Note that your actual benefit will be reduced should you
                        have a Qualified Domestic Relations Order (QDRO)
                        attached to your benefit. Such reductions are outside
                        the scope of this retirement planning calculator.
                    </p>
                </div>
            ),
        },
    },
}

const Brand: IBrand = {
    imgUrl: "/assets/img/local701IBEW-logo-2.png",
    brandName: "NECA/IBEW Local 145",
    contactPhone: "309-757-7550",
    contactEmail: "shanethomas@rjleellp.com",
    landingPageGreeting:
        "Welcome to the Local 145 Retirement Planning Calculator",
    introductionVideos: [
        {
            caption: "Introduction to the Retirement App",
            url: "https://youtube.com/embed/bQeESe_Zqd4",
        },
        {
            caption: "(pdf of script)",
            url: "/content/Local XYZ intro slides_with words 8.1.2018.pdf",
            suppressLine: true,
        },
    ],
    dashboardVideos: [],
    ssaVideos: [
        {
            caption: "Introduction to the Social Security Optimizer",
            url: "https://youtube.com/embed/-coRrBRdt2w",
        },
        {
            caption: "(pdf of script)",
            url: "/content/SSA/Intro to the SS optimizer_w notes_7.192018.pdf",
            suppressLine: true,
        },
        {
            caption: "Knowledge Is Power",
            url: "https://www.youtube.com/embed/56LudX1hui0",
        },
        {
            caption: "(pdf of script)",
            url: "/content/SSA/SS Knowledge is power_w notes_7.19.2018.pdf",
            suppressLine: true,
        },
    ],
    savingsMemberTitleSuffix: "Savings Balance",
    additionalContactInfo: [
        {
            contactEmail: "candacewise@rjleellp.com",
            contactPhone: "309-757-7554",
        },
    ],
    savingsPlanTitle: "Annuity & Profit Sharing Plan Balance",
    markup,
}

function isLockedData(profile: Profile, path: string) {
    const ex = profile.extraInfo

    if (path === ".fixedBalances.retirementSavings") {
        return !_.isUndefined(ex[ColumnNames.profitSharingBalance])
    }

    return false
}

const BrandHelpers: IBrandHelpers = {
    loadProfilePresets,
    memberPensionFormsOfPayment: [
        Local145FormOfPayment.lifeOnly,
        SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
        SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor,
        SimplePensionFormOfPayment.fullJointAndSurvivor,
        Local145FormOfPayment.partialLumpSum,
    ],
    spousalPensionFormsOfPayment: AllSimplePensionFormsOfPayment,
    getPensionInfoNotes: (profile: Profile) => local145Info,
    isLockedData: isLockedData,
    hasIncomingSavingsBalance: true,
    showNonCoveredPriorPensionLanguage: false,
}

const local145Info = [
    {
        url: "/content/local145/Pension-SPD-effective-9-1-2014-Final.pdf",
        linkText: "Local 145 Summary Plan Description",
    },
    {
        url: "/content/SPD_revision_8-19_IO-PBF.pdf",
        linkText: "2019 IO Pension Fund-Summary Plan Description 08-2019",
    },
    { url: "/content/NEBF%20SPD%202021.PDF", linkText: "NEBF 2021 SPD" },
]

const Config145: IWebConfig = {
    GATrackingID: "G-MW9FSDP0P0",
    Pipeline,
    WebServices,
    RegistrationInputs,
    Brand,
    BrandHelpers,
}

export { Config145 }



// WEBPACK FOOTER //
// ./src/config/145/index.tsx